import { FC } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link as ChakraLink,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { Logo } from '~/components/logo';
import { SectionCard } from '~/components/section-card';
import { SectionGroup } from '~/components/section-group';
import { supabase } from '~/libs/supabase';

import { setAuthCookie } from '../user-session/utils';

export const logInFormDataSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
});

export type LogInFormData = z.infer<typeof logInFormDataSchema>;

export const LogInForm: FC = () => {
  const router = useRouter();

  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    resolver: zodResolver(logInFormDataSchema),
    shouldUseNativeValidation: false,
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    } as LogInFormData,
  });

  const onSubmit = async (values: LogInFormData) => {
    const { session, error } = await supabase.auth.signIn({
      email: values.email,
      password: values.password,
    });

    if (error) {
      console.error(error);

      toast({
        title: 'An error occurred',
        description: error.message,
        status: 'error',
        isClosable: true,
      });
    } else {
      await setAuthCookie(session, 'SIGNED_IN');
      router.push('/dashboard');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SectionCard title='Log in' footerSlot={<SectionFooter isValid={isValid} isSubmitting={isSubmitting} />}>
        <Stack spacing={8}>
          <SectionGroup>
            <Stack spacing={4}>
              <FormControl isInvalid={Boolean(errors.email)}>
                <FormLabel htmlFor='email'>Email address</FormLabel>
                <Box maxW='sm'>
                  <Input
                    id='email'
                    type='email'
                    {...register('email', {
                      required: 'Email is required',
                    })}
                  />
                </Box>
                <FormErrorMessage>
                  <>{errors.email?.message}</>
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={Boolean(errors.password)}>
                <FormLabel htmlFor='password'>Password</FormLabel>
                <Box maxW='sm'>
                  <Input
                    id='password'
                    type='password'
                    {...register('password', {
                      required: 'Password is required',
                    })}
                  />
                </Box>
                <FormErrorMessage>
                  <>{errors.password?.message}</>
                </FormErrorMessage>
              </FormControl>
              <Link href='/reset-password' passHref>
                <ChakraLink fontSize='xs' fontWeight='bold' color='medium-gray'>
                  Reset your password
                </ChakraLink>
              </Link>
            </Stack>
          </SectionGroup>
        </Stack>
      </SectionCard>
    </form>
  );
};

const SectionFooter: FC<{ isValid: boolean; isSubmitting: boolean }> = ({ isValid, isSubmitting }) => {
  return (
    <Flex justifyContent='space-between' gap={4} flexDir={{ base: 'column-reverse', lg: 'row' }}>
      <Logo />
      <Flex alignItems='center' gap={4} flexDir={{ base: 'column-reverse', lg: 'row' }}>
        <Text fontSize='xs'>
          Don&apos;t have an account?{' '}
          <Link href='/sign-up' passHref>
            <ChakraLink fontWeight='bold'>Sign up today</ChakraLink>
          </Link>
        </Text>
        <Button isLoading={isSubmitting} disabled={!isValid || isSubmitting} type='submit'>
          Log in
        </Button>
      </Flex>
    </Flex>
  );
};
